import { CommonModule } from '@angular/common'
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms'
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  Output,
  SimpleChanges,
  HostListener,
  ViewChild,
  ElementRef,
  Injector,
  forwardRef,
} from '@angular/core'
import { EluxIcon } from '@elxjs/ui'

export interface Day {
  day: number
  selected?: boolean
  today?: boolean
  disabled: boolean
}
export interface Month {
  value: number
  text: string
  disabled: boolean
}
export interface Year {
  value: number
  disabled: boolean
}
@Component({
  selector: 'app-datepicker',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, EluxIcon],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppDatepicker),
      multi: true,
    },
  ],
  templateUrl: './datepicker.component.html',
  styleUrl: './datepicker.component.scss',
})
export class AppDatepicker implements OnInit, OnChanges, ControlValueAccessor {
  @Input() value: string = ''
  @Input() locale: string = 'pt-BR'
  @Input() idInput: string = ''
  @Input() placeholder: string = '00/00/0000'
  @Input() minDate?: string
  @Input() maxDate?: string
  @Output() dateSelected: EventEmitter<string> = new EventEmitter<string>()
  @Output() invalidDate: EventEmitter<string> = new EventEmitter<string>()
  @ViewChild('datepicker') datepicker!: ElementRef

  disabled = false
  showCalendar = false
  pickerView: 'week' | 'month' | 'year' = 'week'
  currentDate = new Date()
  selectedDate: Date | null = null
  formatedDate: string = ''
  days: Day[] = []
  months: Month[] = []
  years: Year[] = []

  private onChange: Function = () => undefined
  private onTouched: Function = () => undefined
  private parentFormControl!: FormControl

  constructor(private readonly injector: Injector) {}

  injectParentFormControl() {
    const ngControl = this.injector.get(NgControl, null)
    if (ngControl) {
      this.parentFormControl = ngControl.control as FormControl
    }
  }

  private getParentFormControl(): FormControl | undefined {
    if (this.parentFormControl) {
      return this.parentFormControl
    }
    const ngControl = this.injector.get(NgControl, null)
    if (ngControl?.control) {
      return ngControl.control as FormControl
    }
    return undefined
  }

  writeValue(value: string | null): void {
    this.formatedDate = ''
    this.selectedDate = null

    if (!value) {
      return
    }

    const parsed = this.parseInputDate(value)
    if (parsed) {
      this.selectedDate = parsed
      this.currentDate = new Date(parsed)
      this.formatedDate = parsed.toLocaleDateString(this.locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
      this.generateCalendar(this.currentDate)
    } else {
      this.formatedDate = value
    }
  }

  registerOnChange(fn: Function): void {
    this.onChange = fn
  }

  registerOnTouched(fn: Function): void {
    this.onTouched = fn
  }

  get isTouched(): boolean {
    const ctrl = this.getParentFormControl()
    return !!(ctrl?.touched || ctrl?.dirty)
  }

  get isInvalidDate(): boolean {
    const ctrl = this.getParentFormControl()
    return !!ctrl?.invalid
  }

  headerDate() {
    return `${this.currentDate.toLocaleString(this.locale, {
      month: 'long',
    })} ${this.currentDate.getFullYear()}`
  }

  headerDateWeek() {
    const daysOfWeek = []
    const baseDate = new Date()

    for (let i = 0; i < 7; i++) {
      const day = new Date(baseDate.setDate(baseDate.getDate() - baseDate.getDay() + i))
      daysOfWeek.push(day.toLocaleDateString(this.locale, { weekday: 'narrow' }))
    }

    return daysOfWeek
  }

  ngOnInit() {
    this.injectParentFormControl()

    if (this.value) {
      const parsed = this.parseInputDate(this.value)
      if (parsed) {
        this.selectedDate = parsed
        this.currentDate = new Date(parsed)
        this.formatedDate = parsed.toLocaleDateString(this.locale, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      } else {
        this.formatedDate = this.value
      }
    }

    this.generateCalendar(this.currentDate)
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['value'] && !changes['value'].isFirstChange()) {
      const parsed = this.parseInputDate(this.value)
      if (parsed) {
        this.selectedDate = parsed
        this.currentDate = new Date(parsed)
        this.formatedDate = parsed.toLocaleDateString(this.locale, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      } else {
        this.formatedDate = this.value
      }
      this.generateCalendar(this.currentDate)
    }

    // Regenerate calendar when minDate or maxDate changes (except first initialization)
    const minDateChanged = changes['minDate'] && !changes['minDate'].isFirstChange()
    const maxDateChanged = changes['maxDate'] && !changes['maxDate'].isFirstChange()

    if (minDateChanged || maxDateChanged) {
      this.generateCalendar(this.currentDate)
      this.generateMonths()
      this.generateYears()
    }
  }

  onChangeValue(event: Event) {
    const input = event.target as HTMLInputElement
    let value = input.value.replace(/\D/g, '')
    const separator = this.detectLocaleDateSeparator(this.locale)

    if (value.length <= 2) {
      this.formatedDate = value
    } else if (value.length <= 4) {
      this.formatedDate = `${value.slice(0, 2)}${separator}${value.slice(2)}`
    } else {
      this.formatedDate = `${value.slice(0, 2)}${separator}${value.slice(2, 4)}${separator}${value.slice(4, 8)}`
    }

    input.value = this.formatedDate
    input.setSelectionRange(input.value.length, input.value.length)
  }

  detectLocaleDateSeparator(locale: string): string {
    const sampleDate = new Date(2000, 0, 15)
    const formatted = sampleDate.toLocaleDateString(locale)
    const match = formatted.match(/[^0-9]/)
    return match ? match[0] : '/'
  }

  toggleCalendar() {
    this.showCalendar = !this.showCalendar
    if (this.showCalendar) {
      this.pickerView = 'week'
      this.currentDate = this.getOpenCalendarDate()
      this.generateCalendar(this.currentDate)
    }
  }

  changeView() {
    switch (this.pickerView) {
      case 'week':
        this.pickerView = 'month'
        this.generateMonths()
        break
      case 'month':
        this.pickerView = 'year'
        this.generateYears()
        break
      case 'year':
        break
      default:
        break
    }
  }

  closeCalendar() {
    this.showCalendar = false
  }

  previous() {
    switch (this.pickerView) {
      case 'week':
        this.prevMonth()
        break
      case 'month':
        this.prevYear(1)
        break
      case 'year':
        this.prevYear(30)
        break
      default:
        break
    }
  }

  next() {
    switch (this.pickerView) {
      case 'week':
        this.nextMonth()
        break
      case 'month':
        this.nextYear(1)
        break
      case 'year':
        this.nextYear(30)
        break
      default:
        break
    }
  }

  prevMonth() {
    this.currentDate = new Date(this.currentDate.setMonth(this.currentDate.getMonth() - 1))
    this.generateCalendar(this.currentDate)
  }

  nextMonth() {
    this.currentDate = new Date(this.currentDate.setMonth(this.currentDate.getMonth() + 1))
    this.generateCalendar(this.currentDate)
  }

  prevYear(yearQtde: number) {
    this.onChangeYear(this.currentDate.getFullYear() - yearQtde)
    this.generateYears()
  }

  nextYear(yearQtde: number) {
    this.onChangeYear(this.currentDate.getFullYear() + yearQtde)
    this.generateYears()
  }

  selectDate(day: Day) {
    const selected = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), day.day)

    this.selectedDate = selected
    this.formatedDate = this.selectedDate.toLocaleDateString(this.locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })

    this.generateCalendar(this.currentDate)
    this.closeCalendar()
    this.onBlur()
  }

  selectMonth(monthValue: number) {
    const maxDate = this.parseInputDate(this.maxDate)
    const selectedMonth = new Date(this.currentDate.getFullYear(), monthValue, 1)

    if (maxDate && selectedMonth > new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)) {
      return
    }

    this.currentDate = new Date(this.currentDate.getFullYear(), monthValue, 10)
    this.pickerView = 'week'
    this.generateCalendar(this.currentDate)
  }

  onChangeYear(year: number) {
    this.currentDate = new Date(this.currentDate.setFullYear(year))
    this.generateMonths()
  }

  selectYear(year: number) {
    this.onChangeYear(year)
    this.pickerView = 'month'
  }

  generateCalendar(date: Date) {
    this.days = []
    const year = date.getFullYear()
    const month = date.getMonth()
    const minDate = this.parseInputDate(this.minDate)
    const maxDate = this.parseInputDate(this.maxDate)
    const today = new Date()
    const isTodayMonth = today.getMonth() === month && today.getFullYear() === year
    const selectedYear = this.selectedDate?.getFullYear()
    const selectedMonth = this.selectedDate?.getMonth()
    const selectedDay = this.selectedDate?.getDate()

    const firstWeekDayOfMonth = new Date(year, month, 1).getDay()
    const lastWeekDayOfMonth = new Date(year, month + 1, 0).getDate()

    // Preenche os dias do mês anterior, se necessário
    for (let i = firstWeekDayOfMonth; i > 0; i--) {
      this.days.push({ day: 0, disabled: true })
    }

    // Preenche os dias do mês atual
    for (let i = 1; i <= lastWeekDayOfMonth; i++) {
      const dateValue = new Date(year, month, i)
      const isToday = isTodayMonth && today.getDate() === i
      const isSelected = selectedYear === year && selectedMonth === month && selectedDay === i
      const disabled = ((minDate && dateValue < minDate) || (maxDate && dateValue > maxDate)) ?? false
      this.days.push({ day: i, today: isToday, selected: isSelected, disabled: disabled })
    }
  }

  generateMonths() {
    this.months = []
    const currentYear = this.currentDate.getFullYear()
    const maxDate = this.parseInputDate(this.maxDate)
    const minDate = this.parseInputDate(this.minDate)

    for (let i = 0; i < 12; i++) {
      // Usar o dia 1 para evitar problemas com meses que não têm 31 dias
      const dateValue = new Date(currentYear, i, 1)
      const minMonth = minDate ? new Date(minDate.getFullYear(), minDate.getMonth(), 1) : null
      const maxMonth = maxDate ? new Date(maxDate.getFullYear(), maxDate.getMonth(), 1) : null
      const isDisabled = ((minMonth && dateValue < minMonth) || (maxMonth && dateValue > maxMonth)) ?? false
      this.months.push({
        value: i,
        text: dateValue.toLocaleString(this.locale, { month: 'long' }),
        disabled: isDisabled,
      })
    }
  }

  generateYears() {
    this.years = []
    const currentYear = this.currentDate.getFullYear()
    const maxDate = this.parseInputDate(this.maxDate)
    const minDate = this.parseInputDate(this.minDate)

    for (let i = currentYear - 19; i <= currentYear + 10; i++) {
      const isDisabled = ((minDate && i < minDate.getFullYear()) || (maxDate && i > maxDate.getFullYear())) ?? false
      this.years.push({ value: i, disabled: isDisabled })
    }
  }

  validDate(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime())
  }

  onBlur(): void {
    const order = this.detectDateOrder(this.locale)
    const separator = this.detectDateSeparator(this.formatedDate)

    const parts = this.formatedDate.split(separator).map(Number)

    let isValid = false
    let date: Date | undefined = undefined

    if (parts.length === 3 && !parts.some(isNaN)) {
      const dateMap: { [key: string]: number } = {}
      ;[dateMap[order[0]], dateMap[order[1]], dateMap[order[2]]] = parts

      const { day, month, year } = dateMap as { day: number; month: number; year: number }

      date = new Date(year, month - 1, day)

      const minDateObject: Date | null = this.parseInputDate(this.minDate)
      const maxDateObject: Date | null = this.parseInputDate(this.maxDate)

      const isCalendarValid =
        this.validDate(date) && date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day

      const dateTime = date.getTime()

      const isAfterMin = !minDateObject || dateTime >= minDateObject.getTime()
      const isBeforeMax = !maxDateObject || dateTime <= maxDateObject.getTime()

      isValid = isCalendarValid && isAfterMin && isBeforeMax
    }

    this.onTouched()
    this.onChange(this.formatedDate)

    const ctrl = this.getParentFormControl()
    const currentErrors = (ctrl?.errors as Record<string, unknown>) || {}

    if (isValid && date) {
      const { isInvalidDate, ...otherErrors } = currentErrors
      const updatedErrors = Object.keys(otherErrors).length ? otherErrors : null

      ctrl?.setErrors(updatedErrors)
      ctrl?.updateValueAndValidity({ onlySelf: true })
      this.dateSelected.emit(this.formatedDate)
      this.selectedDate = date
      this.generateCalendar(this.currentDate)
    } else {
      this.invalidDate.emit(this.formatedDate)
      ctrl?.setErrors({ ...currentErrors, eluxInvalidDate: true })
    }
  }

  private detectDateOrder(locale: string): string[] {
    const sample = new Date(2025, 10, 23) // 23/11/2025
    const formatted = new Intl.DateTimeFormat(locale).format(sample)

    const parts = formatted.match(/\d+/g)
    if (!parts || parts.length !== 3) return ['year', 'month', 'day'] // fallback

    const order: string[] = []
    for (const part of parts) {
      if (part === '23') order.push('day')
      else if (part === '11') order.push('month')
      else if (part === '2025') order.push('year')
    }

    return order
  }

  private detectDateSeparator(dateStr: string): string {
    if (dateStr.includes('/')) return '/'
    if (dateStr.includes('-')) return '-'
    return '' // fallback
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event) {
    const target = event.target as HTMLElement
    if (!target.closest('.date-picker') && !target.closest('.month-body') && !target.closest('.year-body')) {
      this.showCalendar = false
    }
  }

  isPreviousDisabled(): boolean {
    if (!this.minDate) {
      return false
    }

    const minDate = this.parseInputDate(this.minDate)
    if (!minDate) {
      return false
    }
    switch (this.pickerView) {
      case 'week':
        return (
          this.currentDate.getFullYear() === minDate.getFullYear() && this.currentDate.getMonth() === minDate.getMonth()
        )
      case 'month':
        return this.currentDate.getFullYear() === minDate.getFullYear()
      case 'year':
        return this.currentDate.getFullYear() - 30 < minDate.getFullYear()
      default:
        return false
    }
  }

  isNextDisabled(): boolean {
    if (!this.maxDate) {
      return false
    }

    const maxDate = this.parseInputDate(this.maxDate)
    if (!maxDate) {
      return false
    }
    switch (this.pickerView) {
      case 'week':
        return (
          this.currentDate.getFullYear() === maxDate.getFullYear() && this.currentDate.getMonth() === maxDate.getMonth()
        )
      case 'month':
        return this.currentDate.getFullYear() === maxDate.getFullYear()
      case 'year':
        return this.currentDate.getFullYear() + 30 > maxDate.getFullYear()
      default:
        return false
    }
  }

  private parseInputDate(value?: string): Date | null {
    if (!value) return null

    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
    if (iso) {
      const year = Number(iso[1])
      const month = Number(iso[2])
      const day = Number(iso[3])
      const date = new Date(year, month - 1, day)
      const isValid =
        !isNaN(date.getTime()) && date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
      return isValid ? date : null
    }

    const fallback = new Date(value)
    return isNaN(fallback.getTime()) ? null : fallback
  }

  private getOpenCalendarDate(): Date {
    const minDate = this.parseInputDate(this.minDate)
    const maxDate = this.parseInputDate(this.maxDate)

    let baseDate = this.selectedDate ? new Date(this.selectedDate) : new Date(this.currentDate)

    if (!this.selectedDate && minDate) {
      baseDate = new Date(minDate)
    }

    if (minDate && baseDate < minDate) {
      baseDate = new Date(minDate)
    }

    if (maxDate && baseDate > maxDate) {
      baseDate = new Date(maxDate)
    }

    return baseDate
  }
}
